/**
 * MedchemRulesEditor.jsx
 *
 * Embedded section in PreferencesPane that lets the medicinal chemist
 * add, validate, and remove their own bioisostere / mitigation /
 * ring-swap rules. Built-in rules (from `medchemRules.js`) are shown
 * read-only for reference. User rules persist via `gibbs/preferences.js`
 * (server-backed; survives reload).
 *
 * Phase 3 of gibbs-medchem-design-handlers.
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Accordion, AccordionSummary, AccordionDetails,
    Box, Typography, TextField, Button, IconButton, Stack, Chip,
    Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    listAllBioisosteres,
    addUserBioisostere,
    removeUserBioisostere,
    addUserMitigation,
    removeUserMitigation,
    addUserRingSwap,
    removeUserRingSwap,
    validateSmartsPattern,
} from '../../gibbs/knowledge/medchemRuleLookup';
import { getPreference } from '../../gibbs/preferences';
import { TOXICOPHORE_RULES } from '../../gibbs/knowledge/medchemRules';
import { HETEROAROMATIC_SWAPS } from '../../gibbs/knowledge/compoundTransform';

// ─── Helpers ───────────────────────────────────────────────────────────────

function emptyBioisostereDraft() {
    return {
        id: '', label: '', fromPattern: '', toFragment: '', citation: '',
    };
}

function emptyMitigationDraft() {
    return {
        alertId: '', label: '', mitigationPattern: '', toFragment: '', citation: '',
    };
}

function emptyRingSwapDraft() {
    return {
        fromRing: '', toRing: '', fromPattern: '', toFragment: '', citation: '', label: '',
    };
}

// ─── Sub-component: AddRuleForm ────────────────────────────────────────────

function AddRuleForm({
    fields, draft, setDraft, onSave, onCancel, smartsField,
}) {
    const [validation, setValidation] = useState({ status: 'idle', error: null });
    const [saving, setSaving] = useState(false);

    const handleValidate = useCallback(async () => {
        const smarts = draft[smartsField];
        if (!smarts) {
            setValidation({ status: 'invalid', error: 'SMARTS field is required' });
            return;
        }
        setValidation({ status: 'validating', error: null });
        const result = await validateSmartsPattern(smarts);
        setValidation({
            status: result.valid ? 'valid' : 'invalid',
            error: result.error,
        });
    }, [draft, smartsField]);

    const handleSave = useCallback(async (skipValidation = false) => {
        if (!skipValidation && validation.status !== 'valid') {
            // Try to validate on the way in if user clicked Save without Validate
            const result = await validateSmartsPattern(draft[smartsField]);
            if (!result.valid) {
                setValidation({ status: 'invalid', error: result.error });
                return;
            }
        }
        setSaving(true);
        try {
            await onSave(draft);
        } finally {
            setSaving(false);
        }
    }, [draft, validation.status, onSave, smartsField]);

    return (
        <Box
            sx={{
                p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1,
            }}
        >
            <Stack spacing={1}>
                {fields.map((f) => (
                    <TextField
                        key={f.key}
                        label={f.label}
                        value={draft[f.key] || ''}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        size="small"
                        fullWidth
                        multiline={f.multiline}
                        helperText={f.helperText}
                    />
                ))}
                {validation.status === 'valid' && (
                    <Alert severity="success" sx={{ py: 0 }}>SMARTS parses cleanly.</Alert>
                )}
                {validation.status === 'invalid' && validation.error && (
                    <Alert severity="error" sx={{ py: 0 }}>{`Invalid: ${validation.error}`}</Alert>
                )}
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleValidate}
                        disabled={validation.status === 'validating' || saving}
                    >
                        Validate
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSave(false)}
                        disabled={saving}
                    >
                        Save
                    </Button>
                    {validation.status === 'invalid' && validation.error === 'RDKit unavailable' && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => handleSave(true)}
                            disabled={saving}
                        >
                            Save anyway (offline)
                        </Button>
                    )}
                    <Button variant="text" size="small" onClick={onCancel} disabled={saving}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

// ─── Sub-component: BuiltInList ────────────────────────────────────────────

function BuiltInList({ items, columns }) {
    if (!items || items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None encoded yet.
            </Typography>
        );
    }
    return (
        <Stack spacing={0.5}>
            {items.map((item, idx) => (
                <Box
                    // Items in built-in lists are stable read-only rows; index
                    // is fine as a key here (no reordering or removal).
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    sx={{
                        p: 0.5,
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {columns.map((col) => (
                        <Box key={col.key} component="span" sx={{ mr: 2 }}>
                            <strong>{`${col.label}: `}</strong>
                            <code>{String(item[col.key] || '')}</code>
                        </Box>
                    ))}
                </Box>
            ))}
        </Stack>
    );
}

// ─── Sub-component: UserRulesList ──────────────────────────────────────────

function UserRulesList({ rules, columns, onRemove }) {
    if (!rules || rules.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                No user rules yet.
            </Typography>
        );
    }
    return (
        <Stack spacing={0.5}>
            {rules.map((rule) => (
                <Stack
                    key={rule.uniqueKey}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ p: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    {columns.map((col) => (
                        <Box key={col.key} component="span" sx={{ fontSize: '0.85rem', mr: 1 }}>
                            <strong>{`${col.label}: `}</strong>
                            <code>{String(rule[col.key] || '')}</code>
                        </Box>
                    ))}
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                        size="small"
                        aria-label="Remove rule"
                        onClick={() => onRemove(rule)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ))}
        </Stack>
    );
}

// ─── Section: Bioisosteres ─────────────────────────────────────────────────

function BioisostereSection({ refreshToken, onChange }) {
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState(emptyBioisostereDraft());

    const allRules = listAllBioisosteres();
    const builtIn = allRules.filter((r) => r.source === 'built-in');
    const userRules = (getPreference('userBioisosteres') || []).map((r) => ({
        ...r, uniqueKey: r.id,
    }));

    const handleSave = useCallback(async (rule) => {
        if (!rule.id) {
            // eslint-disable-next-line no-alert
            window.alert('Rule id is required');
            return;
        }
        const result = await addUserBioisostere(rule);
        if (result && result.success === false) {
            // eslint-disable-next-line no-alert
            window.alert(`Saved locally but server persistence failed (${result.error}). Rule will not survive a page reload until the prefs endpoint is deployed.`);
        }
        setDraft(emptyBioisostereDraft());
        setAdding(false);
        onChange();
    }, [onChange]);

    const handleRemove = useCallback(async (rule) => {
        await removeUserBioisostere(rule.id);
        onChange();
    }, [onChange]);

    return (
        <Box sx={{ mt: 1 }}>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                        {`Built-in bioisosteres (${builtIn.length}, read-only)`}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <BuiltInList
                        items={builtIn}
                        columns={[
                            { key: 'id', label: 'id' },
                            { key: 'fromPattern', label: 'fromPattern' },
                            { key: 'toFragment', label: 'toFragment' },
                        ]}
                    />
                </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {`Your bioisostere rules (${userRules.length})`}
                </Typography>
                <UserRulesList
                    rules={userRules}
                    columns={[
                        { key: 'label', label: 'label' },
                        { key: 'id', label: 'id' },
                        { key: 'fromPattern', label: 'from' },
                        { key: 'toFragment', label: 'to' },
                    ]}
                    onRemove={handleRemove}
                />
                {!adding && (
                    <Button
                        size="small"
                        sx={{ mt: 0.5 }}
                        onClick={() => setAdding(true)}
                    >
                        + Add bioisostere
                    </Button>
                )}
                {adding && (
                    <Box sx={{ mt: 1 }}>
                        <AddRuleForm
                            draft={draft}
                            setDraft={setDraft}
                            smartsField="fromPattern"
                            fields={[
                                { key: 'label', label: 'Label (e.g., my-cooh-amide)' },
                                { key: 'id', label: 'Rule id (unique)' },
                                { key: 'fromPattern', label: 'fromPattern (SMARTS)', helperText: 'e.g., [CX3](=O)[OX2H1]' },
                                { key: 'toFragment', label: 'toFragment (SMILES)', helperText: 'e.g., C(=O)N' },
                                { key: 'citation', label: 'Citation (optional)' },
                            ]}
                            onSave={handleSave}
                            onCancel={() => {
                                setDraft(emptyBioisostereDraft());
                                setAdding(false);
                            }}
                        />
                    </Box>
                )}
            </Box>
            {/* refreshToken triggers re-render when prefs update elsewhere */}
            <Box hidden>{refreshToken}</Box>
        </Box>
    );
}

// ─── Section: Toxicophore Mitigations ──────────────────────────────────────

function MitigationSection({ refreshToken, onChange }) {
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState(emptyMitigationDraft());

    const builtIn = TOXICOPHORE_RULES
        .filter((r) => r.mitigationPattern && r.toFragment)
        .map((r) => ({
            alertId: r.id,
            mitigationPattern: r.mitigationPattern,
            toFragment: r.toFragment,
        }));
    const userRules = (getPreference('userMitigations') || []).map((r) => ({
        ...r, uniqueKey: r.alertId,
    }));

    const handleSave = useCallback(async (rule) => {
        if (!rule.alertId) {
            // eslint-disable-next-line no-alert
            window.alert('Alert id is required');
            return;
        }
        const result = await addUserMitigation(rule);
        if (result && result.success === false) {
            // eslint-disable-next-line no-alert
            window.alert(`Saved locally but server persistence failed (${result.error}). Rule will not survive a page reload until the prefs endpoint is deployed.`);
        }
        setDraft(emptyMitigationDraft());
        setAdding(false);
        onChange();
    }, [onChange]);

    const handleRemove = useCallback(async (rule) => {
        await removeUserMitigation(rule.alertId);
        onChange();
    }, [onChange]);

    return (
        <Box sx={{ mt: 1 }}>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                        {`Built-in toxicophore mitigations (${builtIn.length}, read-only)`}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <BuiltInList
                        items={builtIn}
                        columns={[
                            { key: 'alertId', label: 'alertId' },
                            { key: 'mitigationPattern', label: 'pattern' },
                            { key: 'toFragment', label: 'toFragment' },
                        ]}
                    />
                </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {`Your mitigation rules (${userRules.length})`}
                </Typography>
                <UserRulesList
                    rules={userRules}
                    columns={[
                        { key: 'label', label: 'label' },
                        { key: 'alertId', label: 'alertId' },
                        { key: 'mitigationPattern', label: 'pattern' },
                        { key: 'toFragment', label: 'to' },
                    ]}
                    onRemove={handleRemove}
                />
                {!adding && (
                    <Button
                        size="small"
                        sx={{ mt: 0.5 }}
                        onClick={() => setAdding(true)}
                    >
                        + Add mitigation
                    </Button>
                )}
                {adding && (
                    <Box sx={{ mt: 1 }}>
                        <AddRuleForm
                            draft={draft}
                            setDraft={setDraft}
                            smartsField="mitigationPattern"
                            fields={[
                                { key: 'label', label: 'Label' },
                                { key: 'alertId', label: 'Alert id (matches a toxicophore rule id)' },
                                { key: 'mitigationPattern', label: 'mitigationPattern (SMARTS)' },
                                { key: 'toFragment', label: 'toFragment (SMILES)' },
                                { key: 'citation', label: 'Citation (optional)' },
                            ]}
                            onSave={handleSave}
                            onCancel={() => { setDraft(emptyMitigationDraft()); setAdding(false); }}
                        />
                    </Box>
                )}
            </Box>
            <Box hidden>{refreshToken}</Box>
        </Box>
    );
}

// ─── Section: Ring Swaps ───────────────────────────────────────────────────

function RingSwapSection({ refreshToken, onChange }) {
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState(emptyRingSwapDraft());

    const builtIn = Object.entries(HETEROAROMATIC_SWAPS).map(([name, entry]) => ({
        ring: name,
        pattern: entry.pattern,
        fragment: entry.fragment,
    }));
    const userRules = (getPreference('userRingSwaps') || []).map((r) => ({
        ...r, uniqueKey: `${r.fromRing}->${r.toRing}`,
    }));

    const handleSave = useCallback(async (rule) => {
        if (!rule.fromRing || !rule.toRing) {
            // eslint-disable-next-line no-alert
            window.alert('fromRing and toRing are required');
            return;
        }
        const result = await addUserRingSwap(rule);
        if (result && result.success === false) {
            // eslint-disable-next-line no-alert
            window.alert(`Saved locally but server persistence failed (${result.error}). Rule will not survive a page reload until the prefs endpoint is deployed.`);
        }
        setDraft(emptyRingSwapDraft());
        setAdding(false);
        onChange();
    }, [onChange]);

    const handleRemove = useCallback(async (rule) => {
        await removeUserRingSwap(rule.fromRing, rule.toRing);
        onChange();
    }, [onChange]);

    return (
        <Box sx={{ mt: 1 }}>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                        {`Built-in ring systems (${builtIn.length}, read-only)`}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack direction="row" flexWrap="wrap" spacing={0.5}>
                        {builtIn.map((b) => (
                            <Chip key={b.ring} size="small" label={`${b.ring}: ${b.pattern}`} />
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {`Your ring-swap rules (${userRules.length})`}
                </Typography>
                <UserRulesList
                    rules={userRules}
                    columns={[
                        { key: 'fromRing', label: 'from' },
                        { key: 'toRing', label: 'to' },
                        { key: 'fromPattern', label: 'fromPattern' },
                        { key: 'toFragment', label: 'toFragment' },
                    ]}
                    onRemove={handleRemove}
                />
                {!adding && (
                    <Button
                        size="small"
                        sx={{ mt: 0.5 }}
                        onClick={() => setAdding(true)}
                    >
                        + Add ring swap
                    </Button>
                )}
                {adding && (
                    <Box sx={{ mt: 1 }}>
                        <AddRuleForm
                            draft={draft}
                            setDraft={setDraft}
                            smartsField="fromPattern"
                            fields={[
                                { key: 'fromRing', label: 'fromRing (name)' },
                                { key: 'toRing', label: 'toRing (name)' },
                                { key: 'fromPattern', label: 'fromPattern (SMARTS)' },
                                { key: 'toFragment', label: 'toFragment (SMILES)' },
                                { key: 'citation', label: 'Citation (optional)' },
                            ]}
                            onSave={handleSave}
                            onCancel={() => { setDraft(emptyRingSwapDraft()); setAdding(false); }}
                        />
                    </Box>
                )}
            </Box>
            <Box hidden>{refreshToken}</Box>
        </Box>
    );
}

// ─── Top-level component ───────────────────────────────────────────────────

export default function MedchemRulesEditor() {
    // Bumped after every successful save/remove to force a fresh read of
    // user prefs (which are otherwise read at render time, not subscribed).
    const [refreshToken, setRefreshToken] = useState(0);
    const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

    // No-op effect to satisfy lint; kept for future hook of pref-change events.
    useEffect(() => undefined, [refreshToken]);

    return (
        <Box
            sx={{
                mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider',
            }}
        >
            <Typography variant="h6" sx={{ mb: 1 }}>
                Medchem Rules
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Built-in rules ship with the app. Add your own to extend the
                bioisostere, toxicophore-mitigation, and ring-swap catalogs
                used by the medchem-design actions. SMARTS are RDKit-validated
                before save.
            </Typography>
            <BioisostereSection refreshToken={refreshToken} onChange={refresh} />
            <MitigationSection refreshToken={refreshToken} onChange={refresh} />
            <RingSwapSection refreshToken={refreshToken} onChange={refresh} />
        </Box>
    );
}
